import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

import Button from '../../../components/ui/Button';

const GlobalNotificationPanel = ({ settings, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newContact, setNewContact] = useState('');

  const addEmergencyContact = () => {
    if (newContact?.trim()) {
      const currentContacts = settings?.emergencyContacts || [];
      onChange({
        emergencyContacts: [...currentContacts, newContact?.trim()]
      });
      setNewContact('');
    }
  };

  const removeEmergencyContact = (index) => {
    const currentContacts = settings?.emergencyContacts || [];
    onChange({
      emergencyContacts: currentContacts?.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-primary/5 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <Icon name="settings" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-lg">Global Notification Settings</h3>
            <p className="text-sm text-muted-foreground">System-wide preferences and emergency overrides</p>
          </div>
        </div>

        <Icon
          name={isExpanded ? 'chevron-up' : 'chevron-down'}
          size={20}
          className="text-muted-foreground"
        />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-6 space-y-6 bg-card">
          {/* Quiet Hours */}
          <div className="bg-muted/20 border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="moon" size={20} className="text-primary" />
              <h4 className="font-semibold text-foreground">Quiet Hours</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              No notifications will be sent during these hours (applies to all members who have quiet hours enabled)
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="time"
                label="Start Time"
                value={settings?.quietHoursStart}
                onChange={(e) => onChange({ quietHoursStart: e?.target?.value })}
              />
              <Input
                type="time"
                label="End Time"
                value={settings?.quietHoursEnd}
                onChange={(e) => onChange({ quietHoursEnd: e?.target?.value })}
              />
            </div>
          </div>

          {/* Emergency Override */}
          <div className="bg-muted/20 border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name="alert-triangle" size={20} className="text-warning" />
                <h4 className="font-semibold text-foreground">Emergency Override</h4>
              </div>
              <button
                onClick={() => onChange({ emergencyOverride: !settings?.emergencyOverride })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings?.emergencyOverride ? 'bg-primary' : 'bg-muted'
                }`}
                aria-label="Toggle emergency override"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings?.emergencyOverride ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Allow urgent notifications to bypass quiet hours and do-not-disturb settings
            </p>

            {settings?.emergencyOverride && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-foreground">
                  Emergency Contacts (always notified)
                </label>
                <div className="space-y-2">
                  {settings?.emergencyContacts?.map((contact, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-card border border-border rounded"
                    >
                      <span className="text-sm text-foreground">{contact}</span>
                      <button
                        onClick={() => removeEmergencyContact(index)}
                        className="text-destructive hover:text-destructive/80"
                      >
                        <Icon name="x" size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter email or phone number"
                    value={newContact}
                    onChange={(e) => setNewContact(e?.target?.value)}
                    onKeyPress={(e) => {
                      if (e?.key === 'Enter') {
                        addEmergencyContact();
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={addEmergencyContact}
                    disabled={!newContact?.trim()}
                  >
                    <Icon name="plus" size={16} />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Cross-Platform Sync */}
          <div className="bg-muted/20 border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="refresh-cw" size={20} className="text-primary" />
                <div>
                  <h4 className="font-semibold text-foreground">Cross-Platform Synchronization</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sync notification preferences across all devices
                  </p>
                </div>
              </div>
              <button
                onClick={() => onChange({ crossPlatformSync: !settings?.crossPlatformSync })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings?.crossPlatformSync ? 'bg-primary' : 'bg-muted'
                }`}
                aria-label="Toggle cross-platform sync"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings?.crossPlatformSync ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Notification Summary */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="info" size={20} className="text-primary mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-2">Notification Summary</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Quiet hours: {settings?.quietHoursStart} - {settings?.quietHoursEnd}</li>
                  <li>• Emergency override: {settings?.emergencyOverride ? 'Enabled' : 'Disabled'}</li>
                  <li>• Cross-platform sync: {settings?.crossPlatformSync ? 'Enabled' : 'Disabled'}</li>
                  <li>• Emergency contacts: {settings?.emergencyContacts?.length || 0} configured</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalNotificationPanel;